const NOT_SPECIFIED = 'Not specified';
const NOT_AVAILABLE = 'N/A';
const OFFER_CANDIDATE_SENTINEL = 'OFFER';
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const readTrimmedString = (record, key) => {
    const value = record[key];
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
};
const readNestedFormField = (document, formKey, fieldKey) => {
    const form = document[formKey];
    if (!isRecord(form)) {
        return '';
    }
    return readTrimmedString(form, fieldKey);
};
const candidateDisplayName = (document) => {
    const candidate = document.candidate;
    if (!isRecord(candidate)) {
        return '';
    }
    if (readTrimmedString(candidate, 'internal_candidate') === 'Yes') {
        const internalName = readTrimmedString(candidate, 'internalCandidateName');
        if (internalName !== '') {
            return internalName;
        }
    }
    const firstName = readTrimmedString(candidate, 'firstName');
    const surname = readTrimmedString(candidate, 'surname');
    const externalName = [firstName, surname].filter(Boolean).join(' ');
    if (externalName !== '') {
        return externalName;
    }
    return readTrimmedString(candidate, 'internalCandidateName');
};
const formTwoEmployeeName = (document) => {
    const employeeNameSnake = readNestedFormField(document, 'formTwo', 'employee_name');
    if (employeeNameSnake !== '') {
        return employeeNameSnake;
    }
    return readNestedFormField(document, 'formTwo', 'employeeName');
};
const offerCandidateName = (document) => {
    const candidateName = readTrimmedString(document, 'candidateName');
    if (candidateName === '' || candidateName === OFFER_CANDIDATE_SENTINEL) {
        return '';
    }
    return candidateName;
};
/**
 * Best-available person name for a home document card.
 * STARTER/OFFER prefer selected candidate fields; LEAVER/AMEND prefer formTwo.
 */
export const employeeDisplayName = (document) => {
    if (!isRecord(document)) {
        return NOT_AVAILABLE;
    }
    const docType = readTrimmedString(document, 'docType');
    const atr = document.atr;
    if (docType === 'OFFER') {
        const topLevelCandidateName = offerCandidateName(document);
        if (topLevelCandidateName !== '') {
            return topLevelCandidateName;
        }
        const topLevelCandidate = candidateDisplayName(document);
        if (topLevelCandidate !== '') {
            return topLevelCandidate;
        }
        if (isRecord(atr)) {
            const nestedCandidate = candidateDisplayName(atr);
            if (nestedCandidate !== '') {
                return nestedCandidate;
            }
        }
        return NOT_AVAILABLE;
    }
    if (docType === 'STARTER') {
        const selectedCandidate = candidateDisplayName(document);
        if (selectedCandidate !== '') {
            return selectedCandidate;
        }
        // formTwo.employee_name is the leaving/moving employee on replacement roles.
        const formTwoName = formTwoEmployeeName(document);
        if (formTwoName !== '') {
            return formTwoName;
        }
        return NOT_AVAILABLE;
    }
    const formTwoName = formTwoEmployeeName(document);
    if (formTwoName !== '') {
        return formTwoName;
    }
    const candidateName = candidateDisplayName(document);
    if (candidateName !== '') {
        return candidateName;
    }
    if (isRecord(atr)) {
        const nestedFormTwoName = formTwoEmployeeName(atr);
        if (nestedFormTwoName !== '') {
            return nestedFormTwoName;
        }
        const nestedCandidate = candidateDisplayName(atr);
        if (nestedCandidate !== '') {
            return nestedCandidate;
        }
    }
    return NOT_AVAILABLE;
};
/**
 * Type-specific manager name for a home document card.
 * Missing values show "Not specified".
 */
export const managerDisplayName = (document) => {
    if (!isRecord(document)) {
        return NOT_SPECIFIED;
    }
    const docType = readTrimmedString(document, 'docType');
    const atr = document.atr;
    if (docType === 'STARTER') {
        const hiringManager = readNestedFormField(document, 'formOne', 'hiringManagerName');
        return hiringManager !== '' ? hiringManager : NOT_SPECIFIED;
    }
    if (docType === 'OFFER') {
        if (isRecord(atr)) {
            const nestedHiringManager = readNestedFormField(atr, 'formOne', 'hiringManagerName');
            if (nestedHiringManager !== '') {
                return nestedHiringManager;
            }
        }
        const hiringManager = readNestedFormField(document, 'formOne', 'hiringManagerName');
        return hiringManager !== '' ? hiringManager : NOT_SPECIFIED;
    }
    if (docType === 'LEAVER') {
        // Leaver formOne is captured via HM user search ("Your Details").
        // formSeven.line_manager is only the replacement manager for reports of the leaver.
        const hiringManager = readNestedFormField(document, 'formOne', 'name');
        return hiringManager !== '' ? hiringManager : NOT_SPECIFIED;
    }
    if (docType === 'AMEND') {
        // Amend formOne is labeled "Hiring Manager details".
        // formThree.lineManager is only set when amendment type includes Line Manager Change.
        // formNine.line_manager is legacy and unused by the current amend workflow.
        const hiringManager = readNestedFormField(document, 'formOne', 'name');
        if (hiringManager !== '') {
            return hiringManager;
        }
        const formThreeLineManager = readNestedFormField(document, 'formThree', 'lineManager');
        if (formThreeLineManager !== '') {
            return formThreeLineManager;
        }
        const formNineLineManager = readNestedFormField(document, 'formNine', 'line_manager');
        return formNineLineManager !== '' ? formNineLineManager : NOT_SPECIFIED;
    }
    return NOT_SPECIFIED;
};
